import { withLayout } from "@moxy/next-layout";
import { Button, Checkbox, Form, Input, Typography } from "antd";
import { Store } from "antd/lib/form/interface";
import { ArgsProps as NotificationProps } from "antd/lib/notification";
import { NextPage } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Link from "next/link";
import { useRouter } from "next/router";
import { ReactNode, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { Flex } from "~/src/components/flex";
import { Head } from "~/src/components/head";
import { Translation } from "~/src/components/translation";
import { Layout as ApplicantLayout } from "~/src/layouts/applicant";
import { Unauthorized } from "~/src/lib/api";
import * as Applicants from "~/src/lib/api/applicants";
import * as Companies from "~/src/lib/api/companies";
import { Id } from "~/src/lib/api/id";
import * as Jobs from "~/src/lib/api/jobs";
import { getPrivacyPolicyUrl, getTermsOfUseUrl } from "~/src/lib/get-urls";
import { WithLayoutState } from "~/src/lib/layout";
import { useNotification } from "~/src/lib/notification";
import { useSearchParams } from "~/src/lib/use-search-params";
import { getValidationRule } from "~/src/lib/validations/fields-rules";

const i18nNamespaces = ["pages/applicant/apply", "common", "validation"];

const useEmailAuthNotification = (): NotificationProps => {
  const { t } = useTranslation(["pages/applicant/apply"]);

  return {
    type: "info",
    message: t("pages/applicant/apply:notifications.pending-email-auth.title"),
    description: t(
      "pages/applicant/apply:notifications.pending-email-auth.message",
    ),
    duration: 0,
  };
};

type LayoutState = {
  footerContent?: ReactNode;
  jobId?: Id;
};

type Props = WithLayoutState<LayoutState>;

const Page: NextPage<Props> = ({ setLayoutState }: Props) => {
  const { t } = useTranslation(i18nNamespaces);
  const notification = useNotification();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const router = useRouter();
  const { pageSlug, jobSlug } = useSearchParams({
    pageSlug: "string",
    jobSlug: "string",
  });

  const emailAuthNotifParams = useEmailAuthNotification();

  const { data: { data: job } = {}, isLoading: isLoadingJob } = useQuery(
    Jobs.getQueryKey({ slug: jobSlug }),
    () => Jobs.get({ slug: jobSlug }),
  );

  const { data: { data: company } = {} } = useQuery(
    Companies.getQueryKey({ id: job?.companyId }),
    () => Companies.get({ id: job?.companyId }),
    { enabled: Boolean(job) },
  );

  const isSubmitDisabled = isLoadingJob;

  const {
    mutate: createSession,
    isIdle: isMutationIdle,
    isError: isMutationError,
  } = useMutation(Applicants.create, {
    async onSuccess() {
      // Clear local cache since we just create a new authentication.
      queryClient.clear();
      router.push(
        {
          pathname: "/applications/new",
          query: { job: job?.id },
        },
        undefined,
        { shallow: true },
      );
    },
    onError(e) {
      if (e.constructor === Unauthorized) {
        // Notify pending email authentication.
        notification.open(emailAuthNotifParams);
        form.resetFields();
      }
    },
  });

  // Keep the loading cue on after submit unless there was an error.
  const isSubmitting = !(isMutationIdle || isMutationError);

  useEffect(() => {
    if (!jobSlug || !pageSlug) {
      router.push("/404");
      return;
    }
  }, [jobSlug, pageSlug, router]);

  // Add submit button on layout footer content.
  useEffect(() => {
    const submitButton = (
      <Button
        type="primary"
        size="large"
        loading={isSubmitting}
        disabled={isSubmitDisabled}
        onClick={() => form.submit()}
      >
        {isSubmitting ? t("common:loading") : t("pages/applicant/apply:submit")}
      </Button>
    );

    setLayoutState({
      footerContent: submitButton,
      jobId: job?.id,
    });
  }, [form, isSubmitDisabled, isSubmitting, job?.id, setLayoutState, t]);

  const onFinish = (values: Store) => {
    createSession({
      email: values.email?.trim(),
      jobId: job?.id,
    });
  };

  return (
    <>
      <Head
        title={
          job && company
            ? t("pages/applicant/apply:title", {
                job: job.name,
                company: company.name,
              })
            : undefined
        }
      >
        <meta
          name="description"
          content={
            job && company
              ? t("pages/applicant/apply:meta.description", {
                  company: company.name,
                  job: job.name,
                })
              : undefined
          }
        />
      </Head>
      <Flex direction="vertical" gap="md">
        <Typography>
          <h3>{t("pages/applicant/apply:welcome")}</h3>
          <ul>
            <li>{t("pages/applicant/apply:instructions.completed-warning")}</li>
          </ul>
        </Typography>

        <Form form={form} onFinish={onFinish} layout="vertical">
          <Form.Item
            label={t("pages/applicant/apply:email.label")}
            name="email"
            rules={[getValidationRule("email"), { required: true }]}
          >
            <Input size="large" autoComplete="email" type="email" />
          </Form.Item>

          <Form.Item
            name="agreement"
            valuePropName="checked"
            rules={[
              {
                async validator(_, checked) {
                  if (!checked) {
                    throw new Error(
                      t("pages/applicant/apply:agreement.required"),
                    );
                  }
                },
              },
            ]}
          >
            <Checkbox>
              <Translation
                i18nKey={t("pages/applicant/apply:agreement.label")}
                components={{
                  terms: (
                    <Link
                      target="_blank"
                      rel="noopener noreferrer"
                      href={getTermsOfUseUrl()}
                    />
                  ),
                  privacyPolicy: (
                    <Link
                      target="_blank"
                      rel="noopener noreferrer"
                      href={getPrivacyPolicyUrl()}
                    />
                  ),
                }}
              />
            </Checkbox>
          </Form.Item>
        </Form>
      </Flex>
    </>
  );
};

export const getServerSideProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale, i18nNamespaces)),
  },
});

const mapLayoutStateToLayoutTree = ({ footerContent, jobId }: LayoutState) => (
  <ApplicantLayout footerContent={footerContent} jobId={jobId} />
);

export default withLayout(mapLayoutStateToLayoutTree, {})(Page);
